import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { useFetchLocationsQuery }      from "../api/cmsPanelsApi";
import { useFetchCompanyUserDataQuery } from "../api/usersApi";
import {
  useFetchStockCheckSessionsQuery,
  useStartStockCheckSessionMutation,
} from "../api/stockCheckApi";
import { getApiErrorMessage } from "../api/errorUtils";

const MANAGER_ROLES = new Set(["admin", "owner", "manager"]);

const STATUS_COLOR = {
  ACTIVE:    "success",
  COMPLETED: "primary",
  CANCELLED: "secondary",
};

const STATUS_LABEL = {
  ACTIVE:    "Active",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_) {
    return value;
  }
}

function StockCheckSessions() {
  const navigate = useNavigate();

  const [statusFilter,   setStatusFilter]   = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const [showModal,      setShowModal]      = useState(false);
  const [modalLocation,  setModalLocation]  = useState("");
  const [modalNote,      setModalNote]      = useState("");
  const [startError,     setStartError]     = useState(null);

  const { data: locations = [] } = useFetchLocationsQuery();
  const { data: companyUser }    = useFetchCompanyUserDataQuery();

  const currentRole = String(companyUser?.user?.role || "").toLowerCase();
  const canManage   = MANAGER_ROLES.has(currentRole);

  const {
    data:  sessionsData,
    isFetching,
    error: listError,
  } = useFetchStockCheckSessionsQuery({
    status:     statusFilter   || undefined,
    locationId: locationFilter || undefined,
    pageSize:   50,
  });

  const [startSession, { isLoading: isStarting }] = useStartStockCheckSessionMutation();

  const sessions = sessionsData?.items ?? [];

  const openModal = () => {
    setModalLocation(locations.length ? String(locations[0].id) : "");
    setModalNote("");
    setStartError(null);
    setShowModal(true);
  };

  const handleStart = async () => {
    setStartError(null);
    if (!modalLocation) {
      setStartError("Please select a location.");
      return;
    }
    try {
      const result = await startSession({
        locationId: Number(modalLocation),
        note:       modalNote || undefined,
      }).unwrap();
      setShowModal(false);
      navigate(`/admin/stock-check/sessions/${result.session.id}`);
    } catch (err) {
      setStartError(getApiErrorMessage(err, "Failed to start session."));
    }
  };

  return (
    <div className="content sales-premium">
      <Card>
        <CardHeader>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div className="sp-title">Stock Check</div>
              <p className="sp-subtitle">Audit physical inventory against expected quantities</p>
            </div>
            {canManage && (
              <Button color="primary" size="sm" onClick={openModal}>
                <i className="tim-icons icon-simple-add" style={{ marginRight: 5 }} />
                New Stock Check
              </Button>
            )}
          </div>
        </CardHeader>

        <CardBody>
          {/* Filters */}
          <Row>
            <Col lg="3" md="4" sm="6">
              <FormGroup>
                <Label for="scf-status">Status</Label>
                <Input
                  type="select"
                  id="scf-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Input>
              </FormGroup>
            </Col>
            <Col lg="3" md="4" sm="6">
              <FormGroup>
                <Label for="scf-loc">Location</Label>
                <Input
                  type="select"
                  id="scf-loc"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={String(loc.id)}>
                      {loc.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {listError && (
            <Alert color={listError?.status === 403 ? "info" : "danger"} className="mt-1">
              {listError?.status === 403
                ? "Session history is visible to managers only. If you have a direct session link, you can open it."
                : getApiErrorMessage(listError, "Failed to load sessions.")}
            </Alert>
          )}

          {isFetching && !sessions.length ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : sessions.length === 0 && !listError ? (
            <div className="sp-empty">
              <div className="sp-empty-icon">
                <i className="tim-icons icon-zoom-split" />
              </div>
              <p className="sp-empty-title">No sessions yet</p>
              <p className="sp-empty-desc">
                {canManage
                  ? "Start a new stock check to audit your physical inventory against expected quantities."
                  : "No sessions are available. Ask a manager to start a stock check and share the link with you."}
              </p>
              {canManage && (
                <Button color="primary" size="sm" onClick={openModal} className="mt-2">
                  <i className="tim-icons icon-simple-add" style={{ marginRight: 5 }} />
                  New Stock Check
                </Button>
              )}
            </div>
          ) : sessions.length > 0 ? (
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Started</th>
                  <th style={{ textAlign: "right" }}>Items</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Badge color={STATUS_COLOR[s.status] ?? "secondary"} pill>
                        {STATUS_LABEL[s.status] ?? s.status}
                      </Badge>
                    </td>
                    <td>{s.location?.name ?? "—"}</td>
                    <td className="sp-text-sm" style={{ whiteSpace: "nowrap" }}>
                      {formatDate(s.startedAt)}
                    </td>
                    <td style={{ textAlign: "right" }} className="sp-text-muted">
                      {s.totalItems ?? "—"}
                    </td>
                    <td>
                      <Link
                        to={`/admin/stock-check/sessions/${s.id}`}
                        className="btn btn-sm btn-link"
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </CardBody>
      </Card>

      {/* Start session modal */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered>
        <ModalHeader toggle={() => setShowModal(false)}>
          Start New Stock Check
        </ModalHeader>
        <ModalBody>
          <p className="sc-modal-hint">
            Choose the location you want to audit. The system will snapshot current expected stock
            quantities at the moment the session starts.
          </p>

          {startError && <Alert color="danger">{startError}</Alert>}

          <FormGroup>
            <Label for="modal-location">Location *</Label>
            <Input
              type="select"
              id="modal-location"
              value={modalLocation}
              onChange={(e) => setModalLocation(e.target.value)}
            >
              <option value="">— Select a location —</option>
              {locations.map((loc) => (
                <option key={loc.id} value={String(loc.id)}>
                  {loc.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="modal-note">Note (optional)</Label>
            <Input
              type="textarea"
              id="modal-note"
              rows={3}
              value={modalNote}
              onChange={(e) => setModalNote(e.target.value)}
              placeholder="e.g. Monthly full audit, Q2 spot check…"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModal(false)} disabled={isStarting}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleStart} disabled={isStarting || !modalLocation}>
            {isStarting ? <Spinner size="sm" /> : "Start Session"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default StockCheckSessions;
